import type { JSX } from "preact";
import { useEffect, useState } from "preact/hooks";
import { sweCompare } from "@gss/common/sweCompare";
import { BadgeLength } from "@gss/components/BadgeLength";
import { Button } from "@gss/components/Button";
import { Col } from "@gss/components/Col";
import { Container } from "@gss/components/Container";
import {
    Check,
    Collapse,
    Expand,
    Placeholder,
    Remove,
} from "@gss/components/icons";
import { useMessages } from "@gss/components/Messages";
import { Row } from "@gss/components/Row";
import type { MeasurementStat } from "@gss/types/measurementStat";
import { DAO } from "../DAO";
import type { GroupedQuestionnairesPerOrg } from "../types";
import { useTranslation } from "../util/Translation";

interface Props {
    stat: MeasurementStat;
}

interface SelectedQuestionnaire {
    name: string;
    questionnaire: string[];
    version: GroupedQuestionnairesPerOrg;
}

export function DownloadQuestionnaire({ stat }: Props): JSX.Element | null {
    const [versions, setVersions] = useState<GroupedQuestionnairesPerOrg[]>();
    const [selectedVersion, setSelectedVersion] =
        useState<GroupedQuestionnairesPerOrg>();
    const [selectedQuestionnaire, setSelectedQuestionnaire] =
        useState<SelectedQuestionnaire>();
    const t = useTranslation().translation.download;
    const { addError } = useMessages();

    useEffect(() => {
        DAO.export
            .getQuestionnaireVersions(stat.measurement, stat.periods)
            .then((versions) => {
                versions.sort((a, b) => sweCompare(a.name, b.name));
                setVersions(versions);
            })
            .catch(addError);
    }, [stat]);

    const onClickVersion = (version: GroupedQuestionnairesPerOrg) => {
        if (selectedVersion !== version) {
            setSelectedVersion(version);
        } else {
            setSelectedVersion(undefined);
        }
    };

    const isSelected = (
        version: GroupedQuestionnairesPerOrg,
        questionnaire: string[],
    ) => {
        return (
            selectedQuestionnaire?.version === version &&
            selectedQuestionnaire.questionnaire === questionnaire
        );
    };

    const onClickQuestionnaire = (
        version: GroupedQuestionnairesPerOrg,
        questionnaire: string[],
        name: string,
    ) => {
        if (!isSelected(version, questionnaire)) {
            setSelectedQuestionnaire({
                version,
                questionnaire,
                name,
            });
        } else {
            setSelectedQuestionnaire(undefined);
        }
    };

    const download = () => {
        if (selectedQuestionnaire == null) {
            return;
        }
        const selected = selectedQuestionnaire;
        setSelectedQuestionnaire(undefined);
        DAO.export
            .downloadQuestionnaire(
                stat.measurement,
                stat.periods,
                selected.version.orgId,
                selected.questionnaire[0],
                selected.questionnaire[1],
            )
            .catch(addError);
    };

    const renderExpandIcon = (version: GroupedQuestionnairesPerOrg) => {
        if (selectedVersion === version) {
            return <Collapse />;
        }
        return <Expand />;
    };

    const renderSelectedIcon = (
        version: GroupedQuestionnairesPerOrg,
        questionnaire: string[],
    ) => {
        if (isSelected(version, questionnaire)) {
            return <Check />;
        }
        return <Placeholder />;
    };

    if (versions == null) {
        return null;
    }

    const title = t.downloadQuestionnaire;

    return (
        <section aria-label={title}>
            <h2>{title}</h2>

            <Container fluid>
                <Row>
                    <Col>
                        {versions.map((v) => (
                            <div key={v.orgId}>
                                <div>
                                    <span
                                        className="clickable hover-bold"
                                        onClick={() => onClickVersion(v)}
                                    >
                                        {renderExpandIcon(v)} {v.name}
                                        &nbsp;
                                        <BadgeLength>
                                            {v.questionnaires}
                                        </BadgeLength>
                                    </span>
                                </div>
                                <div className="ms-2">
                                    {selectedVersion === v &&
                                        v.questionnaires.map((q) => {
                                            const name = `${q[0]} / ${q[1]}`;
                                            return (
                                                <div key={name}>
                                                    <span
                                                        className="clickable hover-bold"
                                                        onClick={() => {
                                                            onClickQuestionnaire(
                                                                v,
                                                                q,
                                                                name,
                                                            );
                                                        }}
                                                    >
                                                        {renderSelectedIcon(
                                                            v,
                                                            q,
                                                        )}
                                                        &nbsp;
                                                        {name}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                        ))}
                    </Col>

                    <Col>
                        {t.selectedQuestionnaire}

                        <p />
                        {selectedQuestionnaire != null && (
                            <span
                                className="clickable hover-bold"
                                onClick={() =>
                                    setSelectedQuestionnaire(undefined)
                                }
                            >
                                <Remove />
                                &nbsp;
                                {selectedQuestionnaire.version.name}
                                <br />
                                <Placeholder /> {selectedQuestionnaire.name}
                            </span>
                        )}

                        <p />
                        <Button
                            variant="primary"
                            disabled={selectedQuestionnaire == null}
                            onClick={download}
                        >
                            {title}
                        </Button>
                    </Col>
                </Row>
            </Container>
        </section>
    );
}
